import React, { useEffect, useMemo } from "react";
import CreateFolder from "../CreateFolder";
import OwnedFolders from "../OwnedFolders";
import { useFolderStore } from "../../store/folderStore";
import { LoadingCard } from "../../utils/loading";

// Mirrors DashboardFoldersSection, but scoped to a single department.
// GET /file/folder is org-wide (any org member can see any folder — see
// can_view_folder in views.py), so we still fetch the full list and filter
// client-side by department_ids, the same pattern DepartmentPage already
// uses for files. This keeps folders and files scoped consistently: a
// folder only shows up on the department page(s) it was tagged with at
// creation/upload time.
const DepartmentFoldersSection = ({ deptId, deptName, deptColor }) => {
  const folders = useFolderStore((state) => state.folders);
  const fetchFolders = useFolderStore((state) => state.fetchFolders);
  const refreshFolders = useFolderStore((state) => state.refreshFolders);
  const setRefreshFolders = useFolderStore((state) => state.setRefreshFolders);
  const loading = useFolderStore((state) => state.loading);

  useEffect(() => {
    if (refreshFolders) {
      fetchFolders();
      setRefreshFolders(false);
    }
  }, [refreshFolders]);

  useEffect(() => {
    fetchFolders();
  }, []);

  const departmentFolders = useMemo(() => {
    if (!deptId) return [];
    return (folders || []).filter((folder) =>
      (folder.department_ids || []).includes(deptId)
    );
  }, [folders, deptId]);

  return (
    // A bordered, tinted section container so "Folders" reads as a
    // distinct block from "Files" below it, rather than the two lists
    // running together on the page.
    <section
      className="my-4 rounded-2xl border p-4"
      style={{
        borderColor: deptColor ? `${deptColor}33` : "#e5e7eb",
        backgroundColor: deptColor ? `${deptColor}0d` : "#fafafa",
      }}
    >
      <div className="flex flex-row justify-between items-center">
        {/* Header no longer repeats the department name — the page title
            above already says "Marketing Files", so a colored dot is
            enough to tie this section to the department at a glance. */}
        <h2 className="text-xl font-semibold flex items-center gap-2">
          {deptColor && (
            <span
              className="inline-block h-3 w-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: deptColor }}
            />
          )}
          Folders
        </h2>
        <CreateFolder
          listFolders={fetchFolders}
          deptId={deptId}
          deptColor={deptColor}
        />
      </div>
      <div className="my-2 overflow-x-scroll scrollbar-hide">
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 ">
            {Array.from({ length: 4 }).map((_, index) => (
              <LoadingCard key={index} />
            ))}
          </div>
        ) : (
          <>
            {departmentFolders.length === 0 ? (
              <p className="text-center text-sm text-gray-500 py-6">
                No folders yet{deptName ? ` in ${deptName}` : ""}.
              </p>
            ) : (
              <OwnedFolders
                folders={departmentFolders}
                listFolders={fetchFolders}
                deptColor={deptColor}
                deptId={deptId}
              />
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default DepartmentFoldersSection;
